import React, { useEffect, useContext } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import { UserDetailsContext } from 'context/userDetailsContext';
import moment from 'moment-timezone';

const GeneratePaymentPlanV2 = ({
  caseDetails,
  amount,
  paymentPlan,
  setPaymentPlan,
  setNumberOfPayments,
  numberOfPayments,
  selectedFrequency,
  paymentStartDate,
  firstPaymentAmount,
  setSelectedFrequency,
  setPaymentStartDate,
  setFirstPaymentAmount,
  setPaymentDates,
  setFormErrors,
  setPaymentPlanError,
  formErrors,
  paymentPlanError,
  paymentDates,
  selectedRequest,
}) => {
  const userContext = useContext(UserDetailsContext);

  // Example dropdown values
  const frequencyOfPayments = [
    {
      label: 'Weekly',
      value: 'weekly',
    },
    { label: 'Bi-weekly', value: 'biweekly' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Quarterly', value: 'quarterly' },
    { label: 'Yearly', value: 'yearly' },
  ];
  // Utility for fixing decimals
  const fixToTwoDecimal = (num) => {
    if (!num) return null;
    return parseFloat(num).toFixed(2);
  };

  // Reset form errors
  const resetFormErrors = (field) => {
    setFormErrors((prev) => ({ ...prev, [field]: '' }));
  };

  // Generate payment plan
  const generatePlan = () => {
    if (!numberOfPayments || !selectedFrequency || !paymentStartDate || !firstPaymentAmount) {
      setPaymentPlanError({
        ...paymentPlanError,
        payment_plan: 'Please fill all required fields before generating the plan.',
      });
      return;
    }

    const totalAmount = parseFloat(amount) || parseFloat(caseDetails?.billing_preferences?.retainer_amount);
    const firstPayment = parseFloat(firstPaymentAmount);
    const remainingAmount = totalAmount - firstPayment;
    const remainingPayments = Number(numberOfPayments) - 1;
    const amountPerRemainingPayment = remainingPayments > 0 ? remainingAmount / remainingPayments : 0;

    let generated = [];
    const timezone = userContext?.userDetails?.timezone || moment.tz.guess();

    // Parse the paymentStartDate (which is already in timezone-aware format from TimezoneDatePicker)
    // Extract date components in the user's timezone to avoid day shifting
    const startMoment = moment.tz(paymentStartDate, timezone);
    const startYear = startMoment.year();
    const startMonth = startMoment.month();
    const startDate = startMoment.date();

    for (let i = 0; i < Number(numberOfPayments); i++) {
      let formattedDate;

      // For the first payment (i === 0), use the exact start date directly from TimezoneDatePicker
      if (i === 0) {
        formattedDate = paymentStartDate; // Already in correct format from TimezoneDatePicker
      } else {
        // For subsequent payments, increment from start date in the user's timezone
        const paymentMoment = moment.tz(
          {
            year: startYear,
            month: startMonth,
            date: startDate,
            hour: 23,
            minute: 59,
            second: 59,
          },
          timezone
        );

        // Increment date based on frequency
        switch (selectedFrequency) {
          case 'weekly':
            paymentMoment.add(i * 7, 'days');
            break;
          case 'biweekly':
            paymentMoment.add(i * 14, 'days');
            break;
          case 'monthly':
            paymentMoment.add(i, 'months');
            break;
          case 'quarterly':
            paymentMoment.add(i * 3, 'months');
            break;
          case 'yearly':
            paymentMoment.add(i, 'years');
            break;
          default:
            break;
        }

        // Format in timezone-aware format (same as TimezoneDatePicker returns)
        formattedDate = paymentMoment.format();
      }

      generated.push({
        date: formattedDate, // Use timezone-aware format like TimezoneDatePicker returns
        amount: i === 0 ? firstPayment : parseFloat(amountPerRemainingPayment.toFixed(2)), // split remaining amount
      });
    }

    setPaymentDates(generated);
    setPaymentPlanError({});
  };

  useEffect(() => {
    if (caseDetails && !caseDetails?.billing_preferences?.due) {
      const billing = caseDetails?.billing_preferences;
      const plan = billing.payment_plan_details || {};

      setPaymentPlan(billing?.payment_plan);
      setNumberOfPayments(billing.number_of_payments || '');
      setSelectedFrequency(billing.frequency_of_payment || null);
      // Pass date directly from API - TimezoneDatePicker will handle conversion for display
      setPaymentStartDate(plan.payment_starting_date || null);
      setFirstPaymentAmount(plan.first_payment_amount ? parseFloat(plan.first_payment_amount) : null);

      // Pass dates directly from API - TimezoneDatePicker will handle conversion for display
      const dates = (plan.payment_dates || []).map((pd) => ({
        date: pd.date || null, // Keep as string from API, TimezoneDatePicker handles it
        amount: pd.amount ? parseFloat(pd.amount) : null,
      }));
      setPaymentDates(dates);
    } else {
      const billing = selectedRequest;
      const plan = billing?.payment_plan || {};

      // Set top-level payment plan info
      setPaymentPlan(billing?.is_payment_plan);
      setNumberOfPayments(plan?.number_of_payments || '');
      setSelectedFrequency(plan?.frequency_of_payment || null);
      // Pass date directly from API - TimezoneDatePicker will handle conversion for display
      setPaymentStartDate(plan?.payment_starting_date || null);
      setFirstPaymentAmount(plan?.first_payment_amount ? parseFloat(plan.first_payment_amount) : null);

      // Pass dates directly from API - TimezoneDatePicker will handle conversion for display
      const dates = (plan?.payment_plan_data || []).map((pd) => ({
        date: pd?.date || null, // Keep as string from API, TimezoneDatePicker handles it
        amount: pd?.amount ? parseFloat(pd.amount) : null,
      }));

      setPaymentDates(dates);
    }
  }, [caseDetails]);

  return (
    <>
      {/* Toggle Switch */}
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-12 col-md-4 mt-3 p-medium">Payment Plan</div>
        <div className="col-md-7 col-12">
          <label className="switch">
            <input type="checkbox" name="payment_plan" checked={paymentPlan} onChange={(e) => setPaymentPlan(e.target.checked)} />
            <span className="slider round"></span>
          </label>
        </div>
      </div>

      {/* Payment Plan Details */}
      {paymentPlan && (
        <>
          {/* Number of Payments */}
          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-md-5 col-12">
              <div className="p-medium">
                Number of Payments <span className="text-danger">*</span>
              </div>
            </div>
            <div className="col-md-7 col-12">
              <InputText
                value={numberOfPayments}
                onChange={(e) => {
                  setNumberOfPayments(e.target.value);
                  resetFormErrors('payment_number');
                }}
                className="input-shadow w-100 autowidth"
                placeholder="Number of Payments"
                type="number"
              />
              {formErrors?.payment_number && <span className="text-danger">{formErrors.payment_number}</span>}
            </div>
          </div>

          {/* Frequency of Payments */}
          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-md-5 col-12">
              <div className="p-medium">
                Frequency of Payments <span className="text-danger">*</span>
              </div>
            </div>
            <div className="col-md-7 col-12">
              <Dropdown
                options={frequencyOfPayments}
                value={selectedFrequency}
                onChange={(e) => {
                  setSelectedFrequency(e.value);
                  resetFormErrors('payment_frequency');
                }}
                placeholder="Please select"
                className="w-100 autowidth"
                filter
              />
              {formErrors?.payment_frequency && <span className="text-danger">{formErrors.payment_frequency}</span>}
            </div>
          </div>

          {/* Starting Date */}
          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-md-5 col-12 p-medium pe-2">
              Payment Starting Date <span className="text-danger">*</span>
            </div>
            <div className="col-md-7 col-12 d-flex align-items-center">
              <TimezoneDatePicker
                className="input-shadow w-100 autowidth"
                value={paymentStartDate}
                onChange={(value) => {
                  setPaymentStartDate(value);
                  paymentPlanError.start_date = '';
                  setPaymentPlanError({ ...paymentPlanError });
                }}
                monthNavigator
                yearNavigator
                yearRange="1920:2070"
                timezone={userContext?.userDetails?.timezone}
                isEndOfDay={true}
              />
              {paymentPlanError?.start_date && <span className="text-danger">{paymentPlanError.start_date}</span>}
            </div>
          </div>

          {/* First Payment */}
          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-md-5 col-12 p-medium pe-2">
              First Payment Amount <span className="text-danger">*</span>
            </div>
            <div className="col-md-7 col-12 d-flex align-items-center gap-2">
              <div className="flex-1">
                <InputNumber
                  className="input-shadow w-100 autowidth"
                  value={firstPaymentAmount}
                  inputId="currency-us"
                  placeholder="Amount"
                  min={0}
                  keyfilter={'pnum'}
                  onValueChange={(e) => {
                    let num = e.target.value;
                    setFirstPaymentAmount(fixToTwoDecimal(num));
                    paymentPlanError.first_amount = '';
                    setPaymentPlanError({ ...paymentPlanError });
                  }}
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                />
                {paymentPlanError?.first_amount && <span className="text-danger">{paymentPlanError.first_amount}</span>}
              </div>
              <Button
                className="p-button p-button-primary"
                label="Generate Plan"
                onClick={generatePlan}
                style={{ whiteSpace: 'nowrap', minWidth: '120px' }}
              />
            </div>
          </div>

          {paymentPlanError?.total && <span className="danger-text">{paymentPlanError.total}</span>}
          {paymentPlanError?.payment_plan && <span className="danger-text">{paymentPlanError.payment_plan}</span>}

          {/* Payment Dates Table */}
          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-md-5 col-12"></div>
            <div className="col-md-7 col-12 time-entries">
              {paymentDates?.length > 0 && (
                <table className="table table-striped card-bg">
                  <thead>
                    <tr>
                      <th>Due Date</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentDates.map((item, id) => (
                      <tr key={id}>
                        <td>
                          <TimezoneDatePicker
                            className="input-shadow"
                            value={item.date}
                            onChange={(value) => {
                              paymentDates[id].date = value;
                              setPaymentDates([...paymentDates]);
                              paymentPlanError[`${id}_date`] = '';
                              setPaymentPlanError({ ...paymentPlanError });
                            }}
                            monthNavigator
                            yearNavigator
                            yearRange="1920:2070"
                            timezone={userContext?.userDetails?.timezone}
                            isEndOfDay={true}
                          />
                          {paymentPlanError?.[`${id}_date`] && <span className="text-danger">{paymentPlanError[`${id}_date`]}</span>}
                        </td>
                        <td>
                          <InputNumber
                            className="input-shadow"
                            inputId="currency-us"
                            value={item.amount}
                            onValueChange={(e) => {
                              paymentDates[id].amount = e.target.value;
                              setPaymentDates([...paymentDates]);
                              paymentPlanError[`${id}_amount`] = '';
                              paymentPlanError.total = '';
                              setPaymentPlanError({ ...paymentPlanError });
                            }}
                            mode="currency"
                            currency="USD"
                            locale="en-US"
                          />
                          {paymentPlanError?.[`${id}_amount`] && <span className="text-danger">{paymentPlanError[`${id}_amount`]}</span>}
                        </td>
                        <td className="align-middle">
                          <i
                            className="icon-delete"
                            onClick={() => {
                              paymentDates.splice(id, 1);
                              setPaymentDates([...paymentDates]);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <span
                role="button"
                className="addfield"
                onClick={() => {
                  paymentDates.push({ date: '', amount: '' });
                  setPaymentDates([...paymentDates]);
                }}
              >
                + Add Date
              </span>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default GeneratePaymentPlanV2;
